import React, {useEffect} from "react";
import {useLocation} from "react-router-dom";
import {Container} from "components/misc/Layouts";
import tw from "twin.macro";
import styled from "styled-components";
import {css} from "styled-components/macro";
import Header from "components/headers/light.js";
import Footer from "components/footers/FiveColumnWithInputForm.js";
import {Helmet} from "react-helmet";

import researchList from "../researchList.json";
import studyList from "../studyList.json";
import {StyledLayout} from "../helpers/AnimationRevealPage";

const HeadingRow = tw.div`flex`;
const SectionHeading = tw.h2`text-3xl sm:text-5xl font-black tracking-wide text-center`
const ContentWithPaddingXl= tw.div`max-w-screen-xl mx-auto py-12 md:py-20`;

const Heading = tw(SectionHeading)`text-gray-900`;
const Posts = tw.div`mt-1 sm:-mr-8 flex flex-wrap`;

const PostContainer = styled.div`
  ${tw`mt-5 w-full sm:w-1/2 lg:w-1/3 sm:pr-8`}
`;


const Post = tw.div`cursor-pointer flex flex-col bg-gray-100 rounded-lg border-2 rounded-lg h-full relative`;
const Image = styled.div`
  ${(props) =>
    css`
      background-image: url("${props.image}");
    `}
  ${tw`h-64 w-full bg-cover bg-center relative rounded-t-md`}
`;
const Info = tw.div`p-8`;
const Category = tw.div`uppercase text-primary-500 text-xs font-bold tracking-widest leading-loose`;
const Title = tw.div`mt-1 font-semibold mb-3 text-2xl text-gray-900 group-hover:text-primary-500 transition duration-300`;
const Description = tw.div``;
const Tag = styled.div`
  ${tw`absolute bg-primary-500 text-white px-3 py-1 rounded-full text-xl font-medium`}
  top: 0.75rem;
  right: 0.75rem;
`;
export default () => {
  const location = useLocation();
  
  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.substring(1));
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [location]);
  
  const papers = [
    ...researchList.sort((a, b) => new Date(b.date) - new Date(a.date)),
  ];
  const studies = [
    ...studyList.sort((a, b) => new Date(b.date) - new Date(a.date)),
  ];

  // Get current section for SEO
  const currentSection = location.hash ? location.hash.substring(1) : 'research';
  const getSEOData = () => {
    switch(currentSection) {
      case 'ongoing-research':
        return {
          title: 'Ongoing Research Studies - Current 3D Motion Analysis Projects | MAI Motion',
          description: 'Explore our current research studies in 3D motion analysis, biomechanics, and musculoskeletal health using advanced markerless motion capture technology.',
          keywords: 'ongoing research, 3D motion analysis, biomechanics studies, markerless motion capture, musculoskeletal research'
        };
      case 'upcoming-study':
        return {
          title: 'Upcoming Research Studies - Future 3D Motion Analysis Projects | MAI Motion',
          description: 'Discover upcoming research studies and clinical trials in 3D motion analysis and biomechanics using MAI Motion technology.',
          keywords: 'upcoming studies, future research, clinical trials, 3D motion analysis, biomechanics'
        };
      case 'research-papers':
        return {
          title: 'Published Research Papers - Scientific Publications on 3D Motion Analysis | MAI Motion',
          description: 'Access peer-reviewed research papers and scientific publications on markerless motion capture, biomechanics, and musculoskeletal analysis.',
          keywords: 'research papers, scientific publications, peer-reviewed studies, motion analysis research, biomechanics papers'
        };
      default:
        return {
          title: 'Research & Publications - Scientific Evidence for 3D Motion Analysis | MAI Motion',
          description: 'Comprehensive research hub featuring ongoing studies, upcoming projects, and published papers on 3D motion analysis and biomechanics.',
          keywords: 'research, publications, 3D motion analysis, biomechanics, markerless motion capture, scientific studies'
        };
    }
  };

  const seoData = getSEOData();
  return (
    <>
      <Helmet>
        <title>{seoData.title}</title>
        <meta name="description" content={seoData.description} />
        <meta name="keywords" content={seoData.keywords} />
        <link rel="canonical" href={`https://maimotion.com/research${location.hash || ''}`} />
        <meta property="og:title" content={seoData.title} />
        <meta property="og:description" content={seoData.description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://maimotion.com/research${location.hash || ''}`} />
        <meta name="twitter:title" content={seoData.title} />
        <meta name="twitter:description" content={seoData.description} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="robots" content="index,follow" />
        <meta name="author" content="MAI Motion Research Team" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "name": seoData.title,
            "description": seoData.description,
            "url": `https://maimotion.com/research${location.hash || ''}`,
            "publisher": {
              "@type": "Organization",
              "name": "MAI Motion",
              "url": "https://maimotion.com"
            },
            "mainEntity": {
              "@type": "ItemList",
              "numberOfItems": papers.length + studies.length,
              "itemListElement": [...studies, ...papers].map((item, index) => ({
                "@type": "ListItem",
                "position": index + 1,
                "item": {
                  "@type": "ScholarlyArticle",
                  "name": item.title,
                  "description": item.shortDescription,
                  "url": item.slug ? `https://maimotion.com/${item.group ? 'study' : 'research'}/${item.slug}` : '#'
                }
              }))
            }
          })}
        </script>
      </Helmet>
      <StyledLayout id="home" style={{ padding: "1.5rem" }} className="App">
        <Header />
      <Container >
        <ContentWithPaddingXl>
            {studies.map((post, index) => {
              const isOngoing = post.group === "Ongoing Research";
              const isUpcoming = post.group === "Upcoming Study";
              const sectionId = isOngoing ? "ongoing-research" : isUpcoming ? "upcoming-study" : null;
              
              return (
                <div key={index} id={sectionId}>
                  <HeadingRow style={{ marginTop: index !== 0 ? "3rem" : 0 }}>
                    <Heading>{post.group}</Heading>
                  </HeadingRow>
                  <Posts>
                    <PostContainer>
                      <Post className="group" as="a" href={`/study/${post.slug}`}>
                        {post.tag && <Tag style={{ zIndex: 11 }}>{post.tag}</Tag>}
                        <Image image={post.image} />
                        <Info>
                          {post.category && <Category>{post.category}</Category>}
                          <Title>{post.title}</Title>
                          {post.shortDescription && (
                            <Description style={{ whiteSpace: 'pre-line' }}>{post.shortDescription}</Description>
                          )}
                        </Info>
                      </Post>
                    </PostContainer>
                  </Posts>
                </div>
              );
            })}
          <div id="research-papers">
            <HeadingRow style={{ marginTop: "4rem" }}>
              <Heading>Research Papers</Heading>
            </HeadingRow>
            <Posts>
              {papers.map((post, index) => (
                <PostContainer key={index}>
                  <Post className="group" as="a" href={`/research/${post.slug}`}>
                    <Image image={post.image} />
                    <Info>
                      {post.category && <Category>{post.category}</Category>}
                      <Title>{post.title}</Title>
                      {post.shortDescription && (
                        <Description>{post.shortDescription}</Description>
                      )}
                    </Info>
                  </Post>
                </PostContainer>
              ))}
            </Posts>
          </div>
        </ContentWithPaddingXl>
      </Container>
      <Footer />
    </StyledLayout>
    </>
  );
};
